import React, { useMemo, useState, useEffect, useRef } from 'react';
import { ChevronUp, ChevronDown, MessageSquare, Send } from 'lucide-react';
import DOMPurify from 'dompurify';
import { getCategoryStyling } from '../../utils/categoryStyling';
import { customerService } from '../../services/customerService';
import { customerCategoriesService } from '../../services/customerCategoriesService';
import { organizationService } from '../../services/organizationService';
import { notesService } from '../../services/notesService';
import { authFetch } from '../../utils/authFetch';
import { useUser } from '../../context/UserContext';
import { UnpaidInvoice } from '../../types/organizations';
import { CustomerCategory } from '../../types/customerCategories';
import RiskProfileModal from '../common/modals/RiskProfileModal';
import InvoiceCardModal from '../common/modals/InvoiceCardModal';
import NotesModal from '../common/modals/NotesModal';
import DataCard from '../common/DataCard';
import { TemplateMessage, composeMessage, composeSubjectLine, generateInvoiceTable, InvoiceData, extractPlainTextFromHTML } from '../../utils/messageComposition';
import { useWhatsAppSend } from '../../hooks/useWhatsAppSend';
import PointOfContactInputModal from '../organization/modals/PointOfContactInputModal';
import ToastNotification from '../ToastNotification';

interface CustomerRubrics {
  ltm_invoices: string;
  receivable: number;
  ltm_value: number;
  first_order_date: string | null;
  first_order_days_ago: string | null;
  dpd: string;
  otpr: string;
  dbt: string;
  ptp?: string;
  credit_risk_profile?: 'NEW' | 'RED' | 'AMBER' | 'GREEN' | 'No Dunning';
  credit_risk_profile_reasonings?: string[];
}


interface CustomerRow {
  id: string;
  name: string;
  xero_contact_id: string;
  email_address?: string;
  invoice_metadata: {
    outstanding_invoice_count: number;
    last_12m_invoice_count: number;
    total_receivable: number;
    last_12m_value: number;
    overdue_invoice_count: number;
    overdue_receivable: number;
    total_invoice_count: number;
    lifetime_value: number;
    current_invoices?: string;
    current_amount?: number;
  };
  rubrics: CustomerRubrics;
  ap_archetype?: {
    id: string;
    name: string;
    description?: string;
    descriptions?: Array<{ key: string; value: any; is_active?: boolean }>;
  };
  governance_overlay?: {
    id: string;
    name: string;
    description?: string;
    descriptions?: Array<{ key: string; value: any; is_active?: boolean }>;
  };
  note_count?: number;
  latest_note?: {
    id: string;
    content: string;
    created_at: string;
    user_name?: string;
    administrator_name?: string;
  };
}

interface SortConfig { field: string; direction: 'asc' | 'desc'; }

interface WeeklyDunningOpStepProps {
  customers: CustomerRow[];
  organizationId: string;
  customerCategories?: CustomerCategory[];
  organization?: any;
  customersInSections?: CustomerInSection[];
  governanceOverlays?: Array<{ id: string; name: string; description?: string }>;
  templateMessages?: TemplateMessage[];
  onCustomerNoteCountChange?: (customerId: string, delta: number) => void;
  publicMode?: boolean;
}

interface CustomerInSection {
  id: string;
  customerId: string;
  name: string;
  sectionType: 'ap_archetype' | 'governance_overlay' | 'custom_behavior';
  sectionId: string;
  isDuplicated: boolean;
}

const WeeklyDunningOpStep: React.FC<WeeklyDunningOpStepProps> = ({ customers, organizationId, customerCategories = [], organization, customersInSections = [], governanceOverlays = [], templateMessages = [], onCustomerNoteCountChange, publicMode = false }) => {
  const { accessToken, setUserAndToken } = useUser();
  
  const [sortConfig, setSortConfig] = useState<SortConfig[]>([{ field: 'name', direction: 'asc' }]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  
  
  // State for customer categories
  const [categories, setCategories] = useState<CustomerCategory[]>(customerCategories);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // Modal states
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [modalReasonings, setModalReasonings] = useState<string[]>([]);
  const [modalProfile, setModalProfile] = useState<'NEW' | 'RED' | 'AMBER' | 'GREEN' | 'No Dunning' | undefined>(undefined);
  const [showArchetypeModal, setShowArchetypeModal] = useState(false);
  const [selectedArchetype, setSelectedArchetype] = useState<any>(null);
  const [showGovernanceModal, setShowGovernanceModal] = useState(false);
  const [selectedGovernance, setSelectedGovernance] = useState<any>(null);
  const [showInvoiceCardModal, setShowInvoiceCardModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<UnpaidInvoice | null>(null);
  const [archetypeCache, setArchetypeCache] = useState<Record<string, any>>({});
  const [governanceCache, setGovernanceCache] = useState<Record<string, any>>({});
  const loadingArchetypesRef = useRef<Set<string>>(new Set());
  const loadingGovernancesRef = useRef<Set<string>>(new Set());
  
  // State for template messages and invoice caching
  const [templateMessagesCache, setTemplateMessagesCache] = useState<Record<string, any[]>>({});
  const [customerInvoices, setCustomerInvoices] = useState<Record<string, any[]>>({});
  const [loadingInvoices, setLoadingInvoices] = useState<Record<string, boolean>>({});
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<{
    customer: CustomerRow;
    messageType: 'statement_of_account' | 'overdue_reminder';
    channel: 'email' | 'whatsapp';
    content: string;
    subject?: string;
    template: TemplateMessage;
    useMultipleInvoicesTemplate?: boolean;
  } | null>(null);
  
  // Point of contact input state
  const [picEmail, setPicEmail] = useState('');
  
  // Notes modal state
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedCustomerForNotes, setSelectedCustomerForNotes] = useState<CustomerRow | null>(null);
  const [customerNotesCount, setCustomerNotesCount] = useState<Record<string, number>>({});
  
  // Point of contact input modal state
  const [showPointOfContactModal, setShowPointOfContactModal] = useState(false);
  const [selectedCustomerForConversation, setSelectedCustomerForConversation] = useState<CustomerRow | null>(null);
  const [picName, setPicName] = useState('');
  const [picPhoneNumber, setPicPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+65');
  const [conversationLoading, setConversationLoading] = useState(false);
  const [conversationError, setConversationError] = useState('');
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [conversationChannel, setConversationChannel] = useState<'email' | 'whatsapp'>('whatsapp');
  const [existingConversations, setExistingConversations] = useState<any[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [useExistingConversation, setUseExistingConversation] = useState(true);
  
  // WhatsApp send hook
  const {
    sendCollectionReminderWA,
    createConversation,
    getCustomerPhoneData,
    checkExistingConversation,
    loading: whatsappLoading,
    error: whatsappError
  } = useWhatsAppSend(organizationId);
  

  // Load customer categories (fetch organization-specific categories with dunning ladder data)
  useEffect(() => {
    if (publicMode) {
      // In public mode, rely on provided customerCategories
      setCategories(customerCategories);
      return;
    }
    
    // If customerCategories are provided as props, use them instead of fetching
    if (customerCategories && customerCategories.length > 0) {
      setCategories(customerCategories);
      return;
    }
    
    // In non-public mode, fetch organization-specific categories if we have accessToken and organizationId
    if (accessToken && organizationId && !loadingCategories) {
      setLoadingCategories(true);
      organizationService.getOrganizationCustomerCategories(organizationId, accessToken, setUserAndToken)
        .then(cats => {
          setCategories(cats);
        })
        .catch((err) => {
          console.error('WeeklyDunningOpStep: Error fetching categories:', err);
        })
        .finally(() => setLoadingCategories(false));
    }
  }, [accessToken, setUserAndToken, publicMode, customerCategories, organizationId]);

  // Filter customers with outstanding invoices and overdue amounts, exclude "No Dunning" customers
  // This matches the database calculation in _calculate_credit_controlling_metadata
  const customersWithOutstanding = useMemo(() => {
    if (!customers || !Array.isArray(customers)) {
      return [];
    }
    const filtered = customers.filter(customer => 
      customer.invoice_metadata?.overdue_invoice_count > 0 &&
      customer.invoice_metadata?.overdue_receivable > 0 &&
      customer.rubrics?.credit_risk_profile !== 'No Dunning'
    );
    
    // Debugging removed
    
    return filtered;
  }, [customers]);

  // Load notes count for all customers
  useEffect(() => {
    if (publicMode) return;
    customersWithOutstanding.forEach(customer => {
      loadCustomerNotesCount(customer.id);
    });
  }, [customersWithOutstanding, publicMode]);


  const sortCustomers = (rows: CustomerRow[], config: SortConfig[]) => {
    if (config.length === 0) return rows;
    return [...rows].sort((a, b) => {
      for (const s of config) {
        let av: any; let bv: any;
        switch (s.field) {
          case 'name':
            av = a.name.toLowerCase();
            bv = b.name.toLowerCase();
            break;
          case 'outstanding_invoice_count':
            av = a.invoice_metadata?.outstanding_invoice_count || 0;
            bv = b.invoice_metadata?.outstanding_invoice_count || 0;
            break;
          case 'overdue_receivable':
            av = a.invoice_metadata?.overdue_receivable || 0;
            bv = b.invoice_metadata?.overdue_receivable || 0;
            break;
          case 'credit_risk_profile':
            // Sort by risk level: RED > AMBER > GREEN > NEW > No Dunning
            const riskOrder = { 'RED': 1, 'AMBER': 2, 'GREEN': 3, 'NEW': 4, 'No Dunning': 5 };
            av = riskOrder[a.rubrics?.credit_risk_profile as keyof typeof riskOrder] || 6;
            bv = riskOrder[b.rubrics?.credit_risk_profile as keyof typeof riskOrder] || 6;
            break;
          case 'overdue_amount':
            av = a.invoice_metadata?.overdue_receivable || 0;
            bv = b.invoice_metadata?.overdue_receivable || 0;
            break;
          case 'overdue_invoices_dpd':
            // Sort by largest DPD value from the DPD string
            const getMaxDpd = (dpdString: string) => {
              if (!dpdString) return 0;
              const dpdMatches = dpdString.match(/D\+(\d+)/g);
              if (!dpdMatches || dpdMatches.length === 0) return 0;
              const dpdValues = dpdMatches.map(match => parseInt(match.replace('D+', '')));
              return Math.max(...dpdValues);
            };
            av = getMaxDpd(a.rubrics?.dpd || '');
            bv = getMaxDpd(b.rubrics?.dpd || '');
            break;
          default:
            return 0;
        }
        if (av < bv) return s.direction === 'asc' ? -1 : 1;
        if (av > bv) return s.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const sorted = useMemo(() => sortCustomers(customersWithOutstanding, sortConfig), [customersWithOutstanding, sortConfig]);

  const startIndex = (currentPage - 1) * pageSize;
  const displayed = sorted.slice(startIndex, startIndex + pageSize);
  const totalPages = Math.ceil(sorted.length / pageSize) || 1;

  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  
  const getSortIcon = (field: string) => {
    const s = sortConfig.find(sc => sc.field === field);
    if (!s) return null;
    return s.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };
  
  const handleSort = (field: string) => {
    const existing = sortConfig.find(s => s.field === field);
    let cfg: SortConfig[];
    if (existing) {
      if (existing.direction === 'asc') { existing.direction = 'desc'; cfg = [...sortConfig]; }
      else { cfg = sortConfig.filter(s => s.field !== field); }
    } else { cfg = [...sortConfig, { field, direction: 'asc' }]; }
    setSortConfig(cfg);
    setCurrentPage(1);
  };

  // Risk profile styling
  const riskChipClasses = (profile?: 'NEW' | 'RED' | 'AMBER' | 'GREEN') => {
    if (!profile) return 'bg-gray-100 text-gray-800 border border-gray-200';
    
    const styling = getCategoryStyling(profile);
    return `${styling.bg} ${styling.text} border border-gray-200`;
  };

  // Get all governance overlays for a customer
  const getCustomerGovernanceOverlays = (customerId: string): Array<{ id: string; name: string; description?: string; isDuplicated: boolean }> => {
    if (!customersInSections || !Array.isArray(customersInSections) || !governanceOverlays || !Array.isArray(governanceOverlays)) {
      return [];
    }
    
    const customerInSections = customersInSections.filter(
      cis => cis.customerId === customerId && cis.sectionType === 'governance_overlay'
    );
    
    return customerInSections.map(cis => {
      const overlay = governanceOverlays.find(go => go.id === cis.sectionId);
      return overlay ? { ...overlay, isDuplicated: cis.isDuplicated } : null;
    }).filter((overlay): overlay is { id: string; name: string; description?: string; isDuplicated: boolean } => overlay !== null);
  };


  // Handle invoice click to show invoice card modal
  const handleInvoiceClick = async (invoiceNumber: string) => {
    if (publicMode) return;
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/organizations/${organizationId}/invoices/${invoiceNumber}`,
        {},
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        const responseData = await response.json();
        setSelectedInvoice(responseData.data.invoice);
        setShowInvoiceCardModal(true);
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
    }
  };

  // Fetch template messages for a risk profile
  const fetchTemplateMessages = async (riskProfile: string): Promise<TemplateMessage[]> => {
    if (templateMessagesCache[riskProfile]) {
      return templateMessagesCache[riskProfile];
    }

    if (publicMode && templateMessages && Array.isArray(templateMessages) && templateMessages.length > 0) {
      // In public mode, use the template messages from props
      const filteredMessages = templateMessages.filter((msg: TemplateMessage) => 
        msg.category === riskProfile && 
        (msg.reminder_type === 'statement_of_account' || msg.reminder_type === 'overdue_reminder')
      );
      
      setTemplateMessagesCache(prev => ({
        ...prev,
        [riskProfile]: filteredMessages
      }));
      
      return filteredMessages;
    }

    try {
      const messages = await organizationService.getStaticTemplateMessages('cursor', accessToken, setUserAndToken);
      const filteredMessages = messages.filter((msg: TemplateMessage) => 
        msg.category === riskProfile && 
        (msg.reminder_type === 'statement_of_account' || msg.reminder_type === 'overdue_reminder')
      );
      
      setTemplateMessagesCache(prev => ({
        ...prev,
        [riskProfile]: filteredMessages
      }));
      
      return filteredMessages;
    } catch (err) {
      // ignore on error
      return [];
    }
  };

  // Fetch customer invoices
  const fetchCustomerInvoices = async (xeroContactId: string) => {
    if (customerInvoices[xeroContactId]) {
      return customerInvoices[xeroContactId];
    }

    setLoadingInvoices(prev => ({ ...prev, [xeroContactId]: true }));

    try {
      if (publicMode) {
        // In public mode, use the public endpoint
        const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000';
        // Generate organization slug using the same logic as backend
        const generateOrgSlug = (name: string) => {
          return name
            .replace(/[^a-zA-Z0-9\s-]/g, '') // Strip special characters except letters, numbers, spaces, and hyphens
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .trim()
            .toLowerCase();
        };
        const orgSlug = organization?.legal_entity_name ? generateOrgSlug(organization.legal_entity_name) : '';
        const response = await fetch(`${baseUrl}/api/v1/public/onboarding/org/${orgSlug}/invoices?xero_contact_id=${xeroContactId}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch customer invoices');
        }
        
        const data = await response.json();
        const invoices = data.data?.invoices || [];
        
        setCustomerInvoices(prev => ({
          ...prev,
          [xeroContactId]: invoices
        }));
        
        return invoices;
      } else {
        // In authenticated mode, use the regular service
        const response = await customerService.getAllInvoices(
          organizationId,
          accessToken,
          setUserAndToken,
          xeroContactId
        );

        const invoices = response.invoices || [];
        setCustomerInvoices(prev => ({
          ...prev,
          [xeroContactId]: invoices
        }));

        return invoices;
      }
    } catch (err) {
      console.error('Error fetching customer invoices:', err);
      setCustomerInvoices(prev => ({
        ...prev,
        [xeroContactId]: []
      }));
      return [];
    } finally {
      setLoadingInvoices(prev => ({ ...prev, [xeroContactId]: false }));
    }
  };

  // Calculate total overdue amounts and expected collection
  const calculateSummary = () => {
    let totalOverdueAmount = 0;
    let expectedCollection = 0;

    // Use customersWithOutstanding to include ALL customers with overdue amounts (matches database total)
    customersWithOutstanding.forEach(customer => {
      const overdueAmount = customer.invoice_metadata?.overdue_receivable || 0;
      const riskProfile = customer.rubrics?.credit_risk_profile;
      
      totalOverdueAmount += overdueAmount;
      
      // Calculate expected collection based on risk profile (only for customers that can be dunned)
      if (riskProfile && riskProfile !== 'No Dunning') {
        let collectionRate = 0;
        switch (riskProfile) {
          case 'GREEN':
            collectionRate = 1.0; // 100%
            break;
          case 'NEW':
            collectionRate = 0.8; // 80%
            break;
          case 'AMBER':
            collectionRate = 0.6; // 60%
            break;
          case 'RED':
            collectionRate = 0.2; // 20%
            break;
          default:
            collectionRate = 0.5; // Default 50% for other profiles
        }
        expectedCollection += overdueAmount * collectionRate;
      }
    });

    return {
      totalOverdueAmount,
      expectedCollection
    };
  };

  // Get dunning ladder from customer categories based on risk profile and current DPD
  const getDunningLadder = (profile?: 'NEW' | 'RED' | 'AMBER' | 'GREEN' | 'No Dunning', dpd?: string) => {
    if (!profile || profile === 'No Dunning') {
      return 'No automated dunning';
    }

    // Find the category by name
    const category = categories.find(cat => cat.name === profile);

    if (!category) {
      // Fallback to default based on profile
      switch (profile) {
        case 'RED':
          return 'Immediate: Phone call + Email + Letter';
        case 'AMBER':
          return 'Day 1: Email, Day 3: Phone call, Day 7: Letter';
        case 'GREEN':
          return 'Day 3: Email, Day 7: Phone call, Day 14: Letter';
        case 'NEW':
          return 'Day 1: Welcome email, Day 5: Follow-up, Day 10: Phone call';
        default:
          return 'Standard: Day 3, 7, 14';
      }
    }

    // Extract dunning ladder entries from category entries
    const entries = category?.entries || category?.category_entries || {};
    
    // Check if entries is an object with dunning_ladder property
    let dunningEntries: any[] = [];
    
    if (typeof entries === 'object' && entries !== null && !Array.isArray(entries)) {
      // If it's an object, look for dunning_ladder entries
      if (entries.dunning_ladder && typeof entries.dunning_ladder === 'object') {
        // dunning_ladder is an object, extract its properties
        const dunningLadder = entries.dunning_ladder;
        dunningEntries = Object.keys(dunningLadder).map(key => ({
          key: key,
          value: dunningLadder[key]
        })).sort((a: any, b: any) => {
          // Sort by the numeric part of the key (d+0, d+3, d+7, etc.)
          const aNum = parseInt(a.key.replace('d+', '').replace('_', '0')) || 0;
          const bNum = parseInt(b.key.replace('d+', '').replace('_', '0')) || 0;
          return aNum - bNum;
        });
      } else {
        // Look for dunning_ladder entries (dunning_ladder.d+3, dunning_ladder.d+7, etc.)
        const dunningKeys = Object.keys(entries).filter(key => 
          key.startsWith('dunning_ladder.') || key === 'dunning_ladder'
        );
        
        if (dunningKeys.length > 0) {
          dunningEntries = dunningKeys.map(key => ({
            key: key,
            value: entries[key]
          })).sort((a: any, b: any) => {
            // Sort by the numeric part of the key (d+0, d+3, d+7, etc.)
            const aNum = parseInt(a.key.replace('dunning_ladder.', '').replace('d+', '').replace('_', '0')) || 0;
            const bNum = parseInt(b.key.replace('dunning_ladder.', '').replace('d+', '').replace('_', '0')) || 0;
            return aNum - bNum;
          });
        }
      }
    } else if (Array.isArray(entries)) {
      // If it's an array, filter as before
      dunningEntries = entries.filter((entry: any) => 
        entry.key === 'dunning_ladder' || entry.key.startsWith('dunning_ladder.')
      ).sort((a: any, b: any) => {
        if (a.key === 'dunning_ladder') return -1;
        if (b.key === 'dunning_ladder') return 1;
        return a.key.localeCompare(b.key);
      });
    }

    if (dunningEntries.length === 0) {
      return 'No dunning ladder defined';
    }

    // If no DPD data, return all dunning steps
    if (!dpd) {
      return dunningEntries.map((entry: any) => {
        if (entry.key === 'dunning_ladder') {
          return `D+0: ${entry.value || 'No description'}`;
        }
        const dayPart = entry.key.replace('dunning_ladder.', '').replace('d+', 'D+');
        return `${dayPart}: ${entry.value || 'No description'}`;
      }).join(', ');
    }

    // Extract the largest D+X value from DPD data
    const dpdMatches = dpd.match(/D\+(\d+)/g);
    if (!dpdMatches || dpdMatches.length === 0) {
      // If no D+X found in DPD, return all dunning steps
      return dunningEntries.map((entry: any) => {
        if (entry.key === 'dunning_ladder') {
          return `D+0: ${entry.value || 'No description'}`;
        }
        const dayPart = entry.key.replace('dunning_ladder.', '').replace('d+', 'D+');
        return `${dayPart}: ${entry.value || 'No description'}`;
      }).join(', ');
    }

    // Find the largest D+X value from DPD
    const dpdValues = dpdMatches.map(match => parseInt(match.replace('D+', '')));
    const maxDpd = Math.max(...dpdValues);

    // Find the closest smaller or equal dunning ladder step
    let closestEntry = null;
    let closestValue = -1;

    for (const entry of dunningEntries) {
      let entryValue = 0;
      
      // Extract numeric value from key (d+3, d+7, d+14, etc.)
      const match = entry.key.match(/d\+(\d+)/);
      if (match) {
        entryValue = parseInt(match[1]);
      } else if (entry.key === 'd+0') {
        entryValue = 0;
      }

      // Check if this entry is the closest smaller or equal to maxDpd
      if (entryValue <= maxDpd && entryValue > closestValue) {
        closestValue = entryValue;
        closestEntry = entry;
      }
    }

    // If no suitable entry found, return the first entry (D+0)
    if (!closestEntry) {
      closestEntry = dunningEntries[0];
    }

    // Format the result
    if (closestEntry.key === 'dunning_ladder') {
      return `D+0: ${closestEntry.value || 'No description'}`;
    }
    const dayPart = closestEntry.key.replace('dunning_ladder.', '').replace('d+', 'D+');
    return `${dayPart}: ${closestEntry.value || 'No description'}`;
  };


  // Handle message view click
  const handleMessageClick = async (customer: CustomerRow, messageType: 'statement_of_account' | 'overdue_reminder', channel: 'email' | 'whatsapp') => {
    const riskProfile = customer.rubrics?.credit_risk_profile;
    if (!riskProfile || riskProfile === 'No Dunning') {
      return;
    }

    try {
      // Fetch template messages for this risk profile (works in both public and authenticated mode)
      const templates = await fetchTemplateMessages(riskProfile);
      const template = templates.find((t: TemplateMessage) => 
        t.reminder_type === messageType && 
        t.proposed_channel?.value?.toLowerCase() === channel
      );

      if (!template) {
        console.error('No template found for', { riskProfile, messageType, channel });
        return;
      }

      // Fetch customer invoices (works in both modes)
      const invoices = await fetchCustomerInvoices(customer.xero_contact_id);

      // Determine if we should use multiple invoices template (same logic as composeMessage)
      const isOverdueReminder = template.reminder_type === 'overdue_reminder';
      const outstandingInvoices = invoices.filter((invoice: InvoiceData) =>
        invoice.status !== 'PAID' && parseFloat(invoice.amount_due || '0') > 0
      );
      
      // Count overdue invoices
      const today = new Date();
      const overdueInvoices = outstandingInvoices.filter((invoice: InvoiceData) => {
        const dueDate = invoice.due_date_at ? new Date(invoice.due_date_at) : null;
        const daysOverdue = dueDate ? Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;
        return daysOverdue > 0;
      });

      const useMultipleInvoicesTemplate = !!(isOverdueReminder && 
        template.proposed_msg_template?.message_multiple_invoices && 
        overdueInvoices.length > 1);

      // Compose the message using shared utility
      const content = composeMessage(template, customer, invoices, organization?.legal_entity_name, channel);
      const subject = composeSubjectLine(template, customer, invoices, organization?.legal_entity_name);

      setSelectedMessage({
        customer,
        messageType,
        channel,
        content,
        subject,
        template,
        useMultipleInvoicesTemplate
      });
      setShowMessageModal(true);
    } catch (err) {
      console.error('Error handling message click:', err);
    }
  };

  // Handle notes modal
  const handleOpenNotesModal = (customer: CustomerRow) => {
    setSelectedCustomerForNotes(customer);
    setShowNotesModal(true);
  };

  const handleCloseNotesModal = () => {
    setShowNotesModal(false);
    setSelectedCustomerForNotes(null);
  };

  // Load notes count for a customer - now using embedded note_count from customer object
  const loadCustomerNotesCount = async (customerId: string) => {
    // Find the customer in the current data and use its embedded note_count
    const customer = customersWithOutstanding.find(c => c.id === customerId);
    if (customer && 'note_count' in customer) {
      setCustomerNotesCount(prev => ({
        ...prev,
        [customerId]: customer.note_count || 0
      }));
    }
  };

  // Risk profile modal handlers
  const openRiskModal = (profile?: 'NEW' | 'RED' | 'AMBER' | 'GREEN', reasonings?: string[]) => {
    setModalProfile(profile);
    const order = [
      /^NEW/i, /^Not NEW/i,
      /^RED/i, /^Not RED/i,
      /^AMBER/i, /^Not AMBER/i,
      /^GREEN/i, /^Not GREEN/i
    ];
    const items = [...(reasonings || [])];
    const ranked = items.sort((a, b) => {
      const ai = order.findIndex((re) => re.test(a));
      const bi = order.findIndex((re) => re.test(b));
      const aIdx = ai === -1 ? 999 : ai;
      const bIdx = bi === -1 ? 999 : bi;
      if (aIdx !== bIdx) return aIdx - bIdx;
      return a.localeCompare(b);
    });
    setModalReasonings(ranked);
    setShowRiskModal(true);
  };

  // Archetype and governance modal handlers (same as UnderstandRubricsStep)
  const handleArchetypeClick = async (archetype: any) => {
    if (archetypeCache[archetype.id]) {
      setSelectedArchetype(archetypeCache[archetype.id]);
      setShowArchetypeModal(true);
      return;
    }

    if (loadingArchetypesRef.current.has(archetype.id)) {
      return;
    }

    loadingArchetypesRef.current.add(archetype.id);

    try {
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/ap-archetypes/${archetype.id}`,
        {},
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        const responseData = await response.json();
        const detailedArchetype = responseData;
        
        setArchetypeCache(prev => ({
          ...prev,
          [archetype.id]: detailedArchetype
        }));
        
        setSelectedArchetype(detailedArchetype);
        setShowArchetypeModal(true);
      } else {
        setSelectedArchetype(archetype);
        setShowArchetypeModal(true);
      }
    } catch (err) {
      console.error('Error fetching archetype details:', err);
      setSelectedArchetype(archetype);
      setShowArchetypeModal(true);
    } finally {
      loadingArchetypesRef.current.delete(archetype.id);
    }
  };

  const handleGovernanceClick = async (governance: any) => {
    if (governanceCache[governance.id]) {
      setSelectedGovernance(governanceCache[governance.id]);
      setShowGovernanceModal(true);
      return;
    }

    if (loadingGovernancesRef.current.has(governance.id)) {
      return;
    }

    loadingGovernancesRef.current.add(governance.id);

    try {
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/governance-overlays/${governance.id}`,
        {},
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        const responseData = await response.json();
        const detailedGovernance = responseData;
        
        setGovernanceCache(prev => ({
          ...prev,
          [governance.id]: detailedGovernance
        }));
        
        setSelectedGovernance(detailedGovernance);
        setShowGovernanceModal(true);
      } else {
        setSelectedGovernance(governance);
        setShowGovernanceModal(true);
      }
    } catch (err) {
      console.error('Error fetching governance overlay details:', err);
      setSelectedGovernance(governance);
      setShowGovernanceModal(true);
    } finally {
      loadingGovernancesRef.current.delete(governance.id);
    }
  };

  // Helper function to get template variables - uses the same logic as composeMessage
  const getTemplateVariables = async (customer: CustomerRow, templateName: string): Promise<Record<string, string>> => {
    try {
      // Fetch customer invoices
      const invoices = await fetchCustomerInvoices(customer.xero_contact_id);
      
      // Get the risk profile to fetch the correct template
      const riskProfile = customer.rubrics?.credit_risk_profile;
      if (!riskProfile || riskProfile === 'No Dunning') {
        return {};
      }

      // Fetch template messages for this risk profile
      const templates = await fetchTemplateMessages(riskProfile);
      const template = templates.find((t: TemplateMessage) => 
        t.reminder_type === 'overdue_reminder' && 
        t.proposed_channel?.value?.toLowerCase() === 'whatsapp'
      );

      if (!template) {
        return {};
      }

      // Use the same logic as composeMessage to determine template variables
      const isOverdueReminder = template.reminder_type === 'overdue_reminder';
      const outstandingInvoices = invoices.filter((invoice: InvoiceData) =>
        invoice.status !== 'PAID' && parseFloat(invoice.amount_due || '0') > 0
      );
      
      // Count overdue invoices
      const today = new Date();
      const overdueInvoices = outstandingInvoices.filter((invoice: InvoiceData) => {
        const dueDate = invoice.due_date_at ? new Date(invoice.due_date_at) : null;
        const daysOverdue = dueDate ? Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;
        return daysOverdue > 0;
      });

      // Use message_multiple_invoices if available and customer has multiple overdue invoices
      const useMultipleInvoicesTemplate = isOverdueReminder && 
        template.proposed_msg_template?.message_multiple_invoices && 
        overdueInvoices.length > 1;

      let templateVariables: Record<string, string> = {};

      if (isOverdueReminder) {
        if (outstandingInvoices.length === 0) {
          // Use dummy data when no outstanding invoices
          templateVariables = {
            'customer_name': customer.name || 'Customer',
            'invoice_number': 'INV-XXX',
            'amount_due': '$YYY.00',
            'overdue_day_count': 'ZZZ'
          };
        } else if (useMultipleInvoicesTemplate) {
          // Use multiple invoices template
          const totalAmountDue = overdueInvoices.reduce((sum: number, invoice: InvoiceData) =>
            sum + parseFloat(invoice.amount_due || '0'), 0
          );

          // Generate od_invoices in the new format: INV-001 ($890.0, 9 Days Overdue), INV-002 ($240.0, 5 Days Overdue)
          const odInvoices = overdueInvoices.map((invoice: InvoiceData) => {
            const dueDate = invoice.due_date_at ? new Date(invoice.due_date_at) : null;
            const daysOverdue = dueDate ? Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;
            return `${invoice.invoice_number} ($${parseFloat(invoice.amount_due || '0').toFixed(1)}, ${daysOverdue} Days Overdue)`;
          }).join(', ');

          templateVariables = {
            'user_name': organization?.legal_entity_name || 'Your Organization',
            'customer_name': customer.name || 'Customer',
            'amount_due': `$${totalAmountDue.toFixed(2)}`,
            'od_invoice_count': overdueInvoices.length.toString(),
            'od_invoices': odInvoices
          };
        } else {
          // Use single invoice template
          const targetInvoice = overdueInvoices.length > 0 ? overdueInvoices[0] : outstandingInvoices[0];
          const dueDate = targetInvoice?.due_date_at ? new Date(targetInvoice.due_date_at) : null;
          const daysOverdue = dueDate ? Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;

          // For WhatsApp messages, show XXX if overdue days is less than 1
          const isWhatsApp = template.proposed_channel?.value?.toLowerCase() === 'whatsapp';
          const overdueDisplay = isWhatsApp && daysOverdue < 1 ? 'XXX' : daysOverdue.toString();

          templateVariables = {
            'customer_name': customer.name || 'Customer',
            'invoice_number': targetInvoice?.invoice_number || 'INV-XXXX',
            'amount_due': targetInvoice ? `$${parseFloat(targetInvoice.amount_due || '0').toFixed(2)}` : '$0.00',
            'overdue_day_count': overdueDisplay
          };
        }
      }

      return templateVariables;
    } catch (err) {
      console.error('Error getting template variables:', err);
      return {};
    }
  };

  // Create conversation with email
  const createEmailConversation = async (
    customerId: string,
    emailAddress: string,
    customerName: string
  ) => {
    try {
      if (!accessToken) {
        return { success: false, error: 'Authentication required' };
      }

      const requestBody: any = {
        organization_id: organizationId,
        pic_contact: { email: emailAddress },
        pic_name: customerName,
        channel: 'email'
      };

      // Always include xero_direct_contact_id if customerId is provided
      // Use force_new flag to create a new conversation even if one exists for this customer
      if (customerId && customerId.trim()) {
        requestBody.xero_direct_contact_id = customerId;
        requestBody.force_new = true;  // Force creation of new conversation
      }

      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/admin/conversations`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        },
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        const conversation = await response.json();
        return {
          success: true,
          conversationId: conversation.id
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.detail || 'Failed to create conversation'
        };
      }
    } catch (err: any) {
      return {
        success: false,
        error: err.message || 'Failed to create conversation'
      };
    }
  };

  // Send email message
  const sendEmailMessage = async (
    conversationId: string,
    subject: string,
    bodyContent: string,
    toEmail?: string
  ) => {
    try {
      if (!accessToken) {
        return { success: false, error: 'Authentication required' };
      }

      // Determine if bodyContent is HTML or plain text
      const isHTML = bodyContent.includes('<') && bodyContent.includes('>');
      const bodyHtml = isHTML ? bodyContent : undefined;
      const bodyText = isHTML ? extractPlainTextFromHTML(bodyContent) : bodyContent;

      const requestBody: any = {
        subject,
        body_text: bodyText
      };

      if (bodyHtml) {
        requestBody.body_html = bodyHtml;
      }

      // Explicitly pass to_email if provided (important when adding email to existing conversation)
      if (toEmail) {
        requestBody.to_email = toEmail.trim();
      }

      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/admin/conversations/${conversationId}/send_email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        },
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        return { success: true, message: 'Email sent successfully' };
      } else {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.detail || 'Failed to send email'
        };
      }
    } catch (err: any) {
      return {
        success: false,
        error: err.message || 'Failed to send email'
      };
    }
  };

  // WhatsApp send handler
  const handleSendWhatsAppMessage = async (customer: CustomerRow, templateName: string, useMultipleInvoicesTemplate?: boolean) => {
    try {
      setConversationLoading(true);
      setConversationError('');

      // Check if there are existing conversations for this customer using xero_direct_contact_id
      const existingConversationsList = await checkExistingConversation(customer.id);
      
      // Always show modal for conversation selection, even if only one conversation exists
      if (existingConversationsList.length > 0) {
        // Show modal for selection (user can choose which conversation or create new)
        setSelectedCustomerForConversation(customer);
        setExistingConversations(existingConversationsList);
        // Pre-select first conversation with WhatsApp, or first conversation if none have WhatsApp
        const conversationsWithWhatsApp = existingConversationsList.filter((conv: any) => conv?.pic_contact?.whatsapp);
        const defaultConv = conversationsWithWhatsApp[0] || existingConversationsList[0];
        setSelectedConversationId(defaultConv?.id || null);
        setUseExistingConversation(true);
        setPicName(defaultConv?.pic_name || customer.name);
        // Prefill phone if available from customer data (only if conversation doesn't have WhatsApp)
        // If conversation has WhatsApp, the modal will show it as uneditable
        if (defaultConv?.pic_contact?.whatsapp) {
          // Don't set phone number - modal will show it as uneditable from selectedConversation
          setCountryCode('+65'); // Default, but won't be shown if conversation has WhatsApp
          setPicPhoneNumber(''); // Empty, modal will use selectedConversation.pic_contact.whatsapp
        } else {
          const phoneData = getCustomerPhoneData(customer);
          setPicPhoneNumber(phoneData?.phoneNumber || '');
          setCountryCode('+65');
        }
        setPicEmail(defaultConv?.pic_contact?.email || customer.email_address || '');
        setConversationChannel('whatsapp');
        setShowPointOfContactModal(true);
        setConversationLoading(false);
        return;
      } else {
        // No existing conversation - check if customer has phone data
        const phoneData = getCustomerPhoneData(customer);
        
        if (phoneData) {
          // Customer has phone data, show modal to let user confirm or create new conversation
          setSelectedCustomerForConversation(customer);
          setExistingConversations([]);
          setSelectedConversationId(null);
          setUseExistingConversation(false);
          setPicName(customer.name);
          setPicPhoneNumber(phoneData.phoneNumber);
          setCountryCode('+65');
          setPicEmail(customer.email_address || '');
          setConversationChannel('whatsapp');
          setShowPointOfContactModal(true);
          setConversationLoading(false);
          return;
        } else {
          // No phone data, show point of contact modal
          setSelectedCustomerForConversation(customer);
          setExistingConversations([]);
          setSelectedConversationId(null);
          setUseExistingConversation(false);
          setPicName(customer.name);
          setPicPhoneNumber('');
          setPicEmail(customer.email_address || '');
          setCountryCode('+65');
          setConversationChannel('whatsapp');
          setShowPointOfContactModal(true);
          setConversationLoading(false);
          return;
        }
      }
    } catch (err: any) {
      setConversationError(err.message || 'Failed to send template message');
      setToastMsg(`Error: ${err.message}`);
      setConversationLoading(false);
    }
  };

  // Email send handler (this is now only used when we have all the info, otherwise we use the modal flow)
  const handleSendEmailMessage = async (customer: CustomerRow, subject: string, content: string, emailAddress: string, picName: string, useMultipleInvoicesTemplate?: boolean) => {
    try {
      setConversationLoading(true);
      setConversationError('');

      // Check if there's an existing conversation for this customer using xero_direct_contact_id
      const existingConversation = await checkExistingConversation(customer.id);
      
      let conversationId: string;
      
      if (existingConversation) {
        // Use existing conversation
        conversationId = existingConversation.id;
      } else {
        // Create conversation with the provided email and pic name
        const conversationResult = await createEmailConversation(
          customer.id,
          emailAddress,
          picName
        );

        if (!conversationResult.success) {
          setToastMsg(`Failed to create conversation: ${conversationResult.error}`);
          return;
        }
        
        conversationId = conversationResult.conversationId!;
      }

      // Send email message
      const result = await sendEmailMessage(conversationId, subject, content, emailAddress);

      if (result.success) {
        setToastMsg('Email sent successfully!');
      } else {
        setToastMsg(`Failed to send email: ${result.error}`);
      }
    } catch (err: any) {
      setConversationError(err.message || 'Failed to send email');
      setToastMsg(`Error: ${err.message}`);
    } finally {
      setConversationLoading(false);
      // Dismiss the message modal after API call (success or error)
      setShowMessageModal(false);
    }
  };

  const handleOpenPointOfContactModal = async (customer: CustomerRow, channel: 'email' | 'whatsapp') => {
    setSelectedCustomerForConversation(customer);
    setConversationError('');
    setConversationChannel(channel);

    // Check if there's an existing conversation for this customer using xero_direct_contact_id
    const existingConversation = await checkExistingConversation(customer.id);
    
    if (existingConversation) {
      // Use existing conversation - extract contact info from pic_contact
      if (channel === 'whatsapp') {
        const phoneNumber = existingConversation.pic_contact?.whatsapp || '';
        setPicPhoneNumber(phoneNumber);
      } else {
        const email = existingConversation.pic_contact?.email || '';
        setPicEmail(email);
      }
      setPicName(customer.name);
    } else {
      // Prefill with customer data if available
      setPicName(customer.name);
      if (channel === 'whatsapp') {
        const phoneData = getCustomerPhoneData(customer);
        setPicPhoneNumber(phoneData?.phoneNumber || '');
        setCountryCode('+65');
        setPicEmail('');
      } else {
        setPicEmail(customer.email_address || '');
        setPicPhoneNumber('');
        setCountryCode('+65');
      }
    }

    setShowPointOfContactModal(true);
  };

  const handleClosePointOfContactModal = () => {
    setShowPointOfContactModal(false);
    setSelectedCustomerForConversation(null);
    setPicName('');
    setPicPhoneNumber('');
    setPicEmail('');
    setCountryCode('+65');
    setConversationError('');
    setConversationChannel('whatsapp');
    setExistingConversations([]);
    setSelectedConversationId(null);
    setUseExistingConversation(true);
  };

  const handleStartConversation = async (useMultipleInvoicesTemplate?: boolean) => {
    if (!selectedCustomerForConversation || !selectedMessage) return;

    setConversationLoading(true);
    setConversationError('');

    try {
      if (!accessToken) {
        setConversationError('Authentication required');
        return;
      }

      if (!picName.trim()) {
        setConversationError('Please enter the Point-of-Contact name');
        return;
      }

      const picNameToUse = picName.trim();
      let conversationId: string;

      if (conversationChannel === 'whatsapp') {
        if (!picPhoneNumber.trim()) {
          setConversationError('Please enter a WhatsApp number');
          return;
        }

        const picContact = `${countryCode}${picPhoneNumber}`;

        // Handle WhatsApp conversation creation/update
        if (useExistingConversation && selectedConversationId) {
          // Find the selected conversation
          const selectedConversation = existingConversations.find((conv: any) => conv.id === selectedConversationId);
          if (!selectedConversation) {
            setConversationError('Selected conversation not found');
            return;
          }
          
          // Update existing conversation with WhatsApp - merge WhatsApp into existing pic_contact
          conversationId = selectedConversation.id;
          
          // Merge WhatsApp into existing pic_contact (preserve email, etc.)
          const existingPicContact = selectedConversation.pic_contact || {};
          const updatedPicContact = {
            ...existingPicContact,
            whatsapp: picContact
          };
          
          // Update the existing conversation to add WhatsApp to pic_contact
          const response = await authFetch(
            `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/admin/conversations`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                organization_id: organizationId,
                pic_contact: updatedPicContact, // Merge with existing contacts
                pic_name: picNameToUse,
                channel: 'whatsapp',
                xero_direct_contact_id: selectedCustomerForConversation.id
              })
            },
            accessToken,
            setUserAndToken
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            setConversationError(errorData.detail || 'Failed to update conversation');
            return;
          }
        } else {
          // Create new conversation for WhatsApp (user selected "Create new conversation")
          // Pass force_new flag to create a new conversation even if one exists for this customer
          const response = await authFetch(
            `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/admin/conversations`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                organization_id: organizationId,
                pic_contact: { whatsapp: picContact }, // Use dict format to match backend expectations
                pic_name: picNameToUse,
                channel: 'whatsapp',
                xero_direct_contact_id: selectedCustomerForConversation.id,
                force_new: true  // Force creation of new conversation
              })
            },
            accessToken,
            setUserAndToken
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            setConversationError(errorData.detail || 'Failed to create conversation');
            return;
          }

          const conversation = await response.json();
          conversationId = conversation.id;
        }

        // Determine the number of overdue invoices to select the correct template
        const riskProfile = selectedCustomerForConversation.rubrics?.credit_risk_profile;
        if (!riskProfile || riskProfile === 'No Dunning') {
          setConversationError('Invalid risk profile for template selection');
          return;
        }

        // Fetch template messages for this risk profile
        const templates = await fetchTemplateMessages(riskProfile);
        const template = templates.find((t: TemplateMessage) => 
          t.reminder_type === 'overdue_reminder' && 
          t.proposed_channel?.value?.toLowerCase() === 'whatsapp'
        );

        if (!template?.proposed_msg_template) {
          setConversationError('WhatsApp template not found for this risk profile');
          return;
        }

        // Use the useMultipleInvoicesTemplate flag passed from modal instead of recalculating
        let templateName: string;
        if (useMultipleInvoicesTemplate && template.proposed_msg_template.message_multiple_invoices_wa_template_name) {
          templateName = template.proposed_msg_template.message_multiple_invoices_wa_template_name;
        } else {
          templateName = template.proposed_msg_template.message_wa_template_name || '';
        }

        if (!templateName) {
          setConversationError('WhatsApp template name not found for this risk profile');
          return;
        }

        // Get template variables
        const templateVariables = await getTemplateVariables(selectedCustomerForConversation, templateName);

        // Send collection reminder with conversation ID and template variables
        const result = await sendCollectionReminderWA(conversationId, templateName, templateVariables);

        if (result.success) {
          setToastMsg('Template message sent successfully!');
          handleClosePointOfContactModal();
          setShowMessageModal(false);
        } else {
          setConversationError(result.error || 'Failed to send message');
        }
      } else {
        // Email channel
        if (!picEmail.trim()) {
          setConversationError('Please enter an email address');
          return;
        }

        // Handle email conversation creation/update
        if (useExistingConversation && selectedConversationId) {
          // Find the selected conversation
          const selectedConversation = existingConversations.find((conv: any) => conv.id === selectedConversationId);
          if (!selectedConversation) {
            setConversationError('Selected conversation not found');
            return;
          }
          
          // Update existing conversation with email - merge email into existing pic_contact
          conversationId = selectedConversation.id;
          
          // Merge email into existing pic_contact (preserve whatsapp, etc.)
          const existingPicContact = selectedConversation.pic_contact || {};
          const updatedPicContact = {
            ...existingPicContact,
            email: picEmail.trim()
          };
          
          // Update the existing conversation to add email to pic_contact
          const response = await authFetch(
            `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/admin/conversations`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                organization_id: organizationId,
                pic_contact: updatedPicContact, // Merge with existing contacts
                pic_name: picNameToUse,
                channel: 'email',
                xero_direct_contact_id: selectedCustomerForConversation.id
              })
            },
            accessToken,
            setUserAndToken
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            setConversationError(errorData.detail || 'Failed to update conversation');
            return;
          }
        } else {
          // Create new conversation for email (user selected "Create new conversation")
          // Pass xero_direct_contact_id to link to customer, but use force_new to create a new conversation
          // This stores pic_contact as {"email": "user-input"} as requested
          const conversationResult = await createEmailConversation(
            selectedCustomerForConversation.id, // Link to the same customer
            picEmail.trim(), // Use the email from the modal input (can be different from customer.email_address)
            picNameToUse
          );

          if (!conversationResult.success) {
            setConversationError(conversationResult.error || 'Failed to create conversation');
            return;
          }

          conversationId = conversationResult.conversationId!;
        }

        // Send email message using the subject and content from selectedMessage
        if (!selectedMessage) {
          setConversationError('Message data not available');
          return;
        }

        // Get the final conversation data to recompose message with correct customer_name
        let finalPicName = picNameToUse;
        let finalXeroDirectContactName = selectedCustomerForConversation.name;
        
        // If using existing conversation, get pic_name and xero_direct_contact_name from conversation
        if (useExistingConversation && selectedConversationId) {
          const selectedConversation = existingConversations.find((conv: any) => conv.id === selectedConversationId);
          if (selectedConversation) {
            finalPicName = selectedConversation.pic_name || picNameToUse;
            // xero_direct_contact_name is from customer, which is already set
          }
        }

        // Recompose message and subject with correct customer_name (pic_name preferred)
        const customerInvoices = await fetchCustomerInvoices(selectedCustomerForConversation.xero_contact_id);
        const recomposedContent = composeMessage(
          selectedMessage.template,
          selectedCustomerForConversation,
          customerInvoices,
          organization?.legal_entity_name,
          'email',
          finalPicName,
          finalXeroDirectContactName
        );
        const recomposedSubject = composeSubjectLine(
          selectedMessage.template,
          selectedCustomerForConversation,
          customerInvoices,
          organization?.legal_entity_name,
          finalPicName,
          finalXeroDirectContactName
        );

        const result = await sendEmailMessage(
          conversationId,
          recomposedSubject || selectedMessage.subject || 'Payment Reminder',
          recomposedContent,
          picEmail.trim() // Pass the email from the modal input
        );

        if (result.success) {
          setToastMsg('Email sent successfully!');
          handleClosePointOfContactModal();
          setShowMessageModal(false);
        } else {
          setConversationError(result.error || 'Failed to send email');
        }
      }
    } catch (err: any) {
      setConversationError(err.message || 'Failed to start conversation');
    } finally {
      setConversationLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start">
          <div className="flex items-center justify-center w-12 h-12 rounded-full text-2xl mr-4 bg-orange-100 text-orange-600">📅</div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Upcoming Dunning Actions</h3>
            <p className="text-sm sm:text-base text-gray-600">
            Here are {customersWithOutstanding.length} customers with overdue invoices requiring dunning actions.
              {(() => {
                try {
                  const redAmber = customersWithOutstanding.filter(c => c.rubrics?.credit_risk_profile === 'RED' || c.rubrics?.credit_risk_profile === 'AMBER');
                  const pct = customersWithOutstanding.length > 0 ? (redAmber.length / customersWithOutstanding.length) * 100 : 0;
                  return (
                    <> The RED/AMBER customers represent <span className="text-red-600 font-bold">{pct.toFixed(1)}%</span> of customers requiring dunning actions.</>
                  );
                } catch (e) {
                  return '';
                }
              })()}
            </p>
          </div>
        </div>
      </div>

      {/* View Mode Switch - Only show on mobile */}
      <div className="flex items-center justify-between mb-4 sm:hidden">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Switch Views :</span>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'table'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Table
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'cards'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Cards
            </button>
          </div>
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 table-auto text-sm sm:text-base">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-48" onClick={() => handleSort('name')}>
                <div className="flex items-center">Customer Name {getSortIcon('name')}</div>
              </th>
              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-60" onClick={() => handleSort('overdue_invoices_dpd')}>
                <div className="flex items-center">Overdue Invoices {getSortIcon('overdue_invoices_dpd')}</div>
              </th>
              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-32" onClick={() => handleSort('overdue_amount')}>
                <div className="flex items-center">Overdue Amount {getSortIcon('overdue_amount')}</div>
              </th>
              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-36" onClick={() => handleSort('credit_risk_profile')}>
                <div className="flex items-center">Credit Risk Profile {getSortIcon('credit_risk_profile')}</div>
              </th>
              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">AP Archetype</th>
              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Governance Overlay</th>
              <th className="px-2 sm:px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider" style={{minWidth: '200px', width: '200px'}}>Current Dunning Ladder</th>
              <th className="px-2 sm:px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-48">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayed.map((c) => {
              return (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-2 sm:px-4 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        {publicMode ? (
                          <span className="text-xs sm:text-sm font-medium text-gray-900">{c.name}</span>
                        ) : (
                          <button
                            onClick={() => window.open(`/organizations/${organizationId}/customers/${c.id}`, '_blank')}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-left flex-1"
                          >
                            {c.name}
                          </button>
                        )}
                        {/* Notes Button hidden in public mode */}
                        {!publicMode && (
                          <button
                            onClick={() => handleOpenNotesModal(c)}
                            className={`p-1 rounded transition-colors ${
                              customerNotesCount[c.id] > 0 
                                ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' 
                                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                            }`}
                            title={customerNotesCount[c.id] > 0 ? `${customerNotesCount[c.id]} note${customerNotesCount[c.id] !== 1 ? 's' : ''}` : 'Add note'}
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 sm:px-4 py-4 text-sm text-gray-900">
                    <div className="max-w-md whitespace-nowrap">
                      {(c.rubrics?.dpd || 'N/A').split(/(\(\w+-\d+\))/g).map((part, index) => {
                        if (part.match(/\(\w+-\d+\)/)) {
                          // Extract invoice number from parentheses
                          const invoiceNumber = part.replace(/[()]/g, '');
                          return publicMode ? (
                            <span key={index} className="text-gray-900">
                              {part}
                            </span>
                          ) : (
                            <button
                              key={index}
                              onClick={() => handleInvoiceClick(invoiceNumber)}
                              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {part}
                            </button>
                          );
                        }
                        return (
                          <span key={index}>
                            {part.split('<br />').map((line, lineIndex, array) => (
                              <React.Fragment key={lineIndex}>
                                {line}
                                {lineIndex < array.length - 1 && <br />}
                              </React.Fragment>
                            ))}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-2 sm:px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(c.invoice_metadata?.overdue_receivable || 0)}
                  </td>
                  <td className="px-2 sm:px-4 py-4 whitespace-nowrap text-sm">
                    <button
                      type="button"
                      onClick={() => {
                        if (c.rubrics?.credit_risk_profile === 'No Dunning') {
                          openRiskModal(undefined, c.rubrics?.credit_risk_profile_reasonings);
                        } else {
                          openRiskModal(c.rubrics?.credit_risk_profile, c.rubrics?.credit_risk_profile_reasonings);
                        }
                      }}
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        c.rubrics?.credit_risk_profile === 'No Dunning'
                          ? 'bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200'
                          : riskChipClasses(c.rubrics?.credit_risk_profile)
                      }`}
                      title={c.rubrics?.credit_risk_profile === 'No Dunning' ? "View dunning reasonings" : "View reasonings"}
                    >
                      {c.rubrics?.credit_risk_profile || 'N/A'}
                    </button>
                  </td>
                  <td className="px-2 sm:px-4 py-4 whitespace-nowrap text-sm">
                    <div className="flex flex-wrap gap-1">
                      {c.ap_archetype ? (
                        <button
                          onClick={() => handleArchetypeClick(c.ap_archetype)}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full hover:bg-blue-200 transition-colors"
                        >
                          {c.ap_archetype?.name}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">N/A</span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 sm:px-4 py-4 whitespace-nowrap text-sm">
                    <div className="flex flex-wrap gap-1">
                      {(() => {
                        const customerOverlays = getCustomerGovernanceOverlays(c.id);
                        if (customerOverlays.length === 0) {
                          return <span className="text-gray-400 text-xs">N/A</span>;
                        }
                        return customerOverlays.map((overlay, index) => (
                          <button
                            key={`${overlay.id}-${index}`}
                            onClick={() => handleGovernanceClick(overlay)}
                            className={`px-2 py-1 text-xs font-medium rounded-full hover:bg-green-200 transition-colors ${
                              'bg-green-100 text-green-800'
                            }`}
                            title={overlay.isDuplicated ? 'Duplicated assignment' : ''}
                          >
                            {overlay.name}
                          </button>
                        ));
                      })()}
                    </div>
                  </td>
                  <td className="px-2 sm:px-4 py-4 text-sm text-gray-900" style={{minWidth: '200px', width: '200px'}}>
                    <div className="text-sm whitespace-normal break-words">
                      {loadingCategories ? (
                        <span className="text-gray-500">Loading dunning ladder...</span>
                      ) : (
                        getDunningLadder(c.rubrics?.credit_risk_profile, c.rubrics?.dpd)
                      )}
                    </div>
                  </td>
                  <td className="px-2 sm:px-4 py-4 whitespace-nowrap text-sm">
                    <div className="flex flex-col space-y-1">
                      {c.rubrics?.credit_risk_profile && c.rubrics.credit_risk_profile !== 'No Dunning' ? (
                        <>
                          <button
                            onClick={() => handleMessageClick(c, 'statement_of_account', 'email')}
                            className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium text-left"
                          >
                            Initial Email
                          </button>
                          <button
                            onClick={() => handleMessageClick(c, 'overdue_reminder', 'whatsapp')}
                            className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium text-left"
                          >
                            Follow-up via WhatsApp
                          </button>
                        </>
                      ) : (
                        <span className="text-gray-400 text-sm">N/A</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Table View Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-700">
              Showing {startIndex + 1} to {Math.min(startIndex + pageSize, sorted.length)} of {sorted.length} customers
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                disabled={currentPage === 1} 
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                if (pageNum > totalPages) return null;
                return (
                  <button 
                    key={pageNum} 
                    onClick={() => setCurrentPage(pageNum)} 
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      currentPage === pageNum 
                        ? 'bg-blue-600 text-white' 
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <span className="px-3 py-2 text-sm text-gray-500">...</span>
              )}
              {totalPages > 5 && currentPage < totalPages && (
                <button 
                  onClick={() => setCurrentPage(totalPages)} 
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {totalPages}
                </button>
              )}
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                disabled={currentPage === totalPages} 
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
        </div>
      )}

      {/* Card View */}
      {viewMode === 'cards' && (
        <div>
        <div className="space-y-3">
          {displayed.map((c) => {
          const customerOverlays = getCustomerGovernanceOverlays(c.id);
          const hasOverdueInvoices = c.rubrics?.dpd && c.rubrics.dpd !== 'N/A';
          const hasApArchetype = c.ap_archetype;
          const hasGovernanceOverlay = customerOverlays.length > 0;
          const hasDunningLadder = c.rubrics?.credit_risk_profile && c.rubrics.credit_risk_profile !== 'No Dunning';
          const hasActions = c.rubrics?.credit_risk_profile && c.rubrics.credit_risk_profile !== 'No Dunning';

          return (
            <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
              {/* Customer Name and Risk Profile */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 truncate">
                    {publicMode ? c.name : (
                      <button
                        onClick={() => window.open(`/organizations/${organizationId}/customers/${c.id}`, '_blank')}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {c.name}
                      </button>
                    )}
                  </h3>
                  <div className="mt-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (c.rubrics?.credit_risk_profile === 'No Dunning') {
                          openRiskModal(undefined, c.rubrics?.credit_risk_profile_reasonings);
                        } else {
                          openRiskModal(c.rubrics?.credit_risk_profile, c.rubrics?.credit_risk_profile_reasonings);
                        }
                      }}
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        c.rubrics?.credit_risk_profile === 'No Dunning'
                          ? 'bg-blue-100 text-blue-800 border border-blue-200'
                          : riskChipClasses(c.rubrics?.credit_risk_profile)
                      }`}
                    >
                      {c.rubrics?.credit_risk_profile || 'N/A'}
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-base font-bold text-gray-900">
                    {formatCurrency(c.invoice_metadata?.overdue_receivable || 0)}
                  </div>
                  <div className="text-xs text-gray-500">Overdue</div>
                </div>
              </div>

              {/* Overdue Invoices - Only show if has data */}
              {hasOverdueInvoices && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-gray-700 mb-1">Overdue Invoices</div>
                  <div className="text-xs text-gray-900">
                    {(() => {
                      const dpdContent = c.rubrics?.dpd || '';
                      // Extract all invoice numbers from parentheses
                      const invoiceMatches = dpdContent.match(/\(\w+-\d+\)/g) || [];
                      // Get the text parts without invoices
                      const textParts = dpdContent.split(/(\(\w+-\d+\))/g);
                      
                      return textParts.map((part, index) => {
                        if (part.match(/\(\w+-\d+\)/)) {
                          const invoiceNumber = part.replace(/[()]/g, '');
                          return publicMode ? (
                            <span key={index} className="text-gray-900">
                              {part}
                            </span>
                          ) : (
                            <button
                              key={index}
                              onClick={() => handleInvoiceClick(invoiceNumber)}
                              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {part}
                            </button>
                          );
                        }
                        // For text parts, replace <br /> with commas and clean up
                        const cleanedText = part.replace(/<br\s*\/?>/g, ', ').trim();
                        if (cleanedText) {
                          return (
                            <span key={index}>
                              {cleanedText}
                            </span>
                          );
                        }
                        return null;
                      }).filter(Boolean);
                    })()}
                  </div>
                </div>
              )}

              {/* AP Archetype and Governance Overlay - Only show if has data */}
              {(hasApArchetype || hasGovernanceOverlay) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                  {hasApArchetype && (
                    <div>
                      <div className="text-xs font-medium text-gray-700 mb-1">AP Archetype</div>
                      <div className="flex flex-wrap gap-1">
                        <button
                          onClick={() => handleArchetypeClick(c.ap_archetype)}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full hover:bg-blue-200 transition-colors"
                        >
                          {c.ap_archetype?.name}
                        </button>
                      </div>
                    </div>
                  )}
                  {hasGovernanceOverlay && (
                    <div>
                      <div className="text-xs font-medium text-gray-700 mb-1">Governance Overlay</div>
                      <div className="flex flex-wrap gap-1">
                        {customerOverlays.map((overlay, index) => (
                          <button
                            key={`${overlay.id}-${index}`}
                            onClick={() => handleGovernanceClick(overlay)}
                            className="px-2 py-1 text-xs font-medium rounded-full hover:bg-green-200 transition-colors bg-green-100 text-green-800"
                            title={overlay.isDuplicated ? 'Duplicated assignment' : ''}
                          >
                            {overlay.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Dunning Ladder - Only show if has data */}
              {hasDunningLadder && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-gray-700 mb-1">Dunning Ladder</div>
                  <div className="text-xs text-gray-900 bg-gray-50 p-2 rounded">
                    {loadingCategories ? (
                      <span className="text-gray-500">Loading dunning ladder...</span>
                    ) : (
                      getDunningLadder(c.rubrics?.credit_risk_profile, c.rubrics?.dpd)
                    )}
                  </div>
                </div>
              )}

              {/* Actions - Only show if has data */}
              {hasActions && (
                <div className="flex gap-4 text-xs">
                  <button
                    onClick={() => handleMessageClick(c, 'statement_of_account', 'email')}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    Initial Email
                  </button>
                  <button
                    onClick={() => handleMessageClick(c, 'overdue_reminder', 'whatsapp')}
                    className="text-green-600 hover:text-green-800 hover:underline font-medium"
                  >
                    Follow-up WhatsApp
                  </button>
                </div>
              )}
            </div>
          );
        })}
        </div>

        {/* Card View Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-xs text-gray-700">
              Showing {startIndex + 1} to {Math.min(startIndex + pageSize, sorted.length)} of {sorted.length} customers
            </div>
            <div className="flex space-x-1">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                disabled={currentPage === 1} 
                className="px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                const pageNum = currentPage <= 2 ? i + 1 : currentPage - 1 + i;
                if (pageNum > totalPages) return null;
                return (
                  <button 
                    key={pageNum} 
                    onClick={() => setCurrentPage(pageNum)} 
                    className={`px-2 py-1 text-xs font-medium rounded ${
                      currentPage === pageNum 
                        ? 'bg-blue-600 text-white' 
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              {totalPages > 3 && currentPage < totalPages - 1 && (
                <span className="px-2 py-1 text-xs text-gray-500">...</span>
              )}
              {totalPages > 3 && currentPage < totalPages && (
                <button 
                  onClick={() => setCurrentPage(totalPages)} 
                  className="px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50"
                >
                  {totalPages}
                </button>
              )}
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                disabled={currentPage === totalPages} 
                className="px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
        </div>
      )}

      {/* Summary Section */}
      {customers.length > 0 && (
        <div className="mt-6 space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg border">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-700">
                <span className="font-medium">Total overdue invoices to collect:</span>
                <span className="ml-2 text-lg font-bold text-gray-900">
                  ${calculateSummary().totalOverdueAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="text-sm text-gray-700">
                <span className="font-medium">Expected to collect in next two weeks:</span>
                <span className="ml-2 text-lg font-bold text-green-600">
                  ${calculateSummary().expectedCollection.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
          
          {/* Collection Assumptions Hint
          <div className="text-xs text-gray-500 text-center">
            Collection assumptions: <span className="text-green-600 font-medium mr-2 ml-2">GREEN 100%</span> <span className="text-purple-600 font-medium mr-2">NEW 80%</span> <span className="text-yellow-600 font-medium mr-2">AMBER 60%</span> <span className="text-red-600 font-medium">RED 20%</span>
          </div> */}
        </div>
      )}


      {customersWithOutstanding.length === 0 && (
        <div className="text-center py-8">
          {(!customers || customers.length === 0) ? (
            // Show loading state when customers data is not yet loaded
            <div>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h4 className="text-lg font-semibold text-gray-700 mb-2">Loading Customer Data</h4>
              <p className="text-gray-600">
                Please wait while we fetch your customer information...
              </p>
            </div>
          ) : (
            // Show no dunning actions required message when data is loaded but no customers require dunning
            <div>
              <div className="text-4xl mb-4">🎉</div>
              <h4 className="text-lg font-semibold text-gray-700 mb-2">No Dunning Actions Required</h4>
              <p className="text-gray-600">
                All customers with overdue invoices are either up to date with their payments or have "No Dunning" status. No dunning actions required this week.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Risk Profile Modal */}
      <RiskProfileModal
        isOpen={showRiskModal}
        onClose={() => setShowRiskModal(false)}
        profile={modalProfile}
        reasonings={modalReasonings}
        organizationId={organizationId}
      />

      {/* Invoice Card Modal */}
      <InvoiceCardModal
        isOpen={showInvoiceCardModal}
        onClose={() => setShowInvoiceCardModal(false)}
        organizationId={organizationId}
        invoice={selectedInvoice}
      />

      {/* AP Archetype Modal */}
      {showArchetypeModal && selectedArchetype && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black opacity-30" 
            onClick={(e) => {
              e.stopPropagation();
              setShowArchetypeModal(false);
            }}
          ></div>
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">AP Archetype Details</h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowArchetypeModal(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <DataCard
                data={selectedArchetype}
                dataType="apArchetype"
                showLink={false}
                compact={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Governance Overlay Modal */}
      {showGovernanceModal && selectedGovernance && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black opacity-30" 
            onClick={(e) => {
              e.stopPropagation();
              setShowGovernanceModal(false);
            }}
          ></div>
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Governance Overlay Details</h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowGovernanceModal(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <DataCard
                data={selectedGovernance}
                dataType="governanceOverlay"
                showLink={false}
                compact={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black opacity-30" 
            onClick={() => setShowMessageModal(false)}
          ></div>
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl mx-auto max-h-[95vh] overflow-auto">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 truncate pr-2">
                  {selectedMessage.messageType === 'statement_of_account' ? 'Initial Email' : 'Follow-up via WhatsApp'} - {selectedMessage.customer.name}
                </h3>
                <button
                  onClick={() => setShowMessageModal(false)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Subject Line - Only for email */}
                {selectedMessage.channel === 'email' && selectedMessage.subject && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                    <div className="bg-gray-50 border border-gray-300 rounded-md p-3 text-sm break-words">
                      {selectedMessage.subject}
                    </div>
                  </div>
                )}

                {/* Message Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {selectedMessage.channel === 'whatsapp' ? 'WhatsApp Message' : 'Email Message'}
                  </label>
                  <div className="bg-gray-50 border border-gray-300 rounded-md p-3 text-sm overflow-x-auto">
                    {selectedMessage.channel === 'email' && selectedMessage.content.includes('<') && selectedMessage.content.includes('>') ? (
                      // Render HTML for email messages
                      <div 
                        className="email-preview text-sm"
                        style={{ 
                          fontFamily: 'Afacad, sans-serif',
                          fontSize: '14px',
                          lineHeight: '1.3',
                          color: '#333'
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: DOMPurify.sanitize(selectedMessage.content, {
                            ALLOWED_TAGS: ['div', 'p', 'br', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'h3', 'strong', 'em', 'b', 'i', 'span'],
                            ALLOWED_ATTR: ['style', 'width', 'cellpadding', 'cellspacing', 'border'],
                            KEEP_CONTENT: true
                          })
                        }}
                      />
                    ) : (
                      // Render plain text for WhatsApp or plain text messages
                      <div className="whitespace-pre-wrap font-mono text-xs" style={{ minWidth: '600px' }}>
                        {selectedMessage.content}
                      </div>
                    )}
                  </div>
                </div>

                {/* Reasoning */}
                {selectedMessage.template.proposed_msg_template?.reasonings && selectedMessage.template.proposed_msg_template.reasonings.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reasoning</label>
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                      <ul className="text-sm text-blue-800 space-y-1">
                        {selectedMessage.template.proposed_msg_template.reasonings.map((reasoning: string, idx: number) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-blue-500 mr-2 flex-shrink-0">•</span>
                            <span className="break-words">{reasoning}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Send Button for WhatsApp templates */}
                {selectedMessage.channel === 'whatsapp' && 
                 selectedMessage.template.proposed_msg_template?.message_wa_template_name && (
                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        // Use the template decision that was already made when opening the modal
                        const template = selectedMessage.template.proposed_msg_template;
                        if (!template) return;
                        
                        // Select the correct WhatsApp template name based on the stored decision
                        const templateName = selectedMessage.useMultipleInvoicesTemplate && template.message_multiple_invoices_wa_template_name
                          ? template.message_multiple_invoices_wa_template_name
                          : template.message_wa_template_name;
                        
                        console.log('Debug - Use multiple template:', selectedMessage.useMultipleInvoicesTemplate);
                        console.log('Debug - Selected template name:', templateName);
                        
                        if (templateName) {
                          handleSendWhatsAppMessage(selectedMessage.customer, templateName, selectedMessage.useMultipleInvoicesTemplate);
                        }
                      }}
                      disabled={conversationLoading || whatsappLoading}
                      className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        conversationLoading || whatsappLoading
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {conversationLoading || whatsappLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send via WhatsApp
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Send Button for Email */}
                {selectedMessage.channel === 'email' && (
                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      onClick={async () => {
                        // Check if there are existing conversations for this customer (any channel)
                        const existingConversationsList = await checkExistingConversation(selectedMessage.customer.id);
                        
                        // Always show modal for conversation selection, even if only one conversation exists
                        if (existingConversationsList.length > 0) {
                          // Show modal for selection (user can choose which conversation or create new)
                          setSelectedCustomerForConversation(selectedMessage.customer);
                          setExistingConversations(existingConversationsList);
                          // Pre-select first conversation with email, or first conversation if none have email
                          const conversationsWithEmail = existingConversationsList.filter((conv: any) => conv?.pic_contact?.email);
                          const defaultConv = conversationsWithEmail[0] || existingConversationsList[0];
                          setSelectedConversationId(defaultConv?.id || null);
                          setUseExistingConversation(true);
                          setPicName(defaultConv?.pic_name || selectedMessage.customer.name);
                          setPicEmail(defaultConv?.pic_contact?.email || selectedMessage.customer.email_address || '');
                          setPicPhoneNumber('');
                          setCountryCode('+65');
                          setConversationChannel('email');
                          setShowPointOfContactModal(true);
                        } else {
                          // No existing conversation, open point of contact modal for new conversation
                          setSelectedCustomerForConversation(selectedMessage.customer);
                          setExistingConversations([]);
                          setSelectedConversationId(null);
                          setUseExistingConversation(false);
                          setPicName(selectedMessage.customer.name);
                          setPicEmail(selectedMessage.customer.email_address || '');
                          setPicPhoneNumber('');
                          setCountryCode('+65');
                          setConversationChannel('email');
                          setShowPointOfContactModal(true);
                        }
                      }}
                      disabled={conversationLoading}
                      className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        conversationLoading
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {conversationLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send via Email
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal (hidden in public mode) */}
      {!publicMode && showNotesModal && selectedCustomerForNotes && (
        <NotesModal
          isOpen={showNotesModal}
          onClose={handleCloseNotesModal}
          entityType="customer"
          entityId={selectedCustomerForNotes.id}
          entityName={selectedCustomerForNotes.name}
          organizationId={organizationId}
          onNoteCountChange={(delta: number) => onCustomerNoteCountChange?.(selectedCustomerForNotes.id, delta)}
        />
      )}

      {/* Point of Contact Input Modal */}
      {showPointOfContactModal && selectedCustomerForConversation && (
        <PointOfContactInputModal
          isOpen={showPointOfContactModal}
          customer={{
            id: selectedCustomerForConversation.id,
            name: selectedCustomerForConversation.name,
            xero_contact_id: selectedCustomerForConversation.xero_contact_id,
            contact_status: 'active',
            addresses: [],
            phones: [],
            agent_analyses: [],
            invoice_metadata: {
              outstanding_invoice_count: selectedCustomerForConversation.invoice_metadata?.outstanding_invoice_count || 0,
              total_invoice_count: selectedCustomerForConversation.invoice_metadata?.total_invoice_count || 0,
              last_12m_invoice_count: selectedCustomerForConversation.invoice_metadata?.last_12m_invoice_count || 0,
              total_receivable: selectedCustomerForConversation.invoice_metadata?.total_receivable || 0,
              lifetime_value: selectedCustomerForConversation.invoice_metadata?.lifetime_value || 0,
              last_12m_value: selectedCustomerForConversation.invoice_metadata?.last_12m_value || 0,
              overdue_invoice_count: selectedCustomerForConversation.invoice_metadata?.overdue_invoice_count || 0,
              overdue_receivable: selectedCustomerForConversation.invoice_metadata?.overdue_receivable || 0
            }
          }}
          channel={conversationChannel}
          onChannelChange={setConversationChannel}
          showChannelSelection={false}
          picName={picName}
          picPhoneNumber={picPhoneNumber}
          picEmail={picEmail}
          countryCode={countryCode}
          conversationLoading={conversationLoading}
          conversationError={conversationError}
          useMultipleInvoicesTemplate={selectedMessage?.useMultipleInvoicesTemplate}
          existingConversations={existingConversations}
          selectedConversationId={selectedConversationId}
          onSelectedConversationIdChange={setSelectedConversationId}
          useExistingConversation={useExistingConversation}
          onClose={handleClosePointOfContactModal}
          onPicNameChange={setPicName}
          onPicPhoneNumberChange={setPicPhoneNumber}
          onPicEmailChange={setPicEmail}
          onCountryCodeChange={setCountryCode}
          onUseExistingConversationChange={setUseExistingConversation}
          onStartConversation={handleStartConversation}
        />
      )}

      {/* Toast Notification */}
      <ToastNotification
        toastMsg={toastMsg}
        setToastMsg={setToastMsg}
      />
    </div>
  );
};

export default WeeklyDunningOpStep;
